import { COUNTRY_CODES } from "../../../../constants/countries";
import { ubicacionDelCodigo } from "./ubicacionesDGI";

/**
 * Los datos de la factura electrónica de Panamá que maneja el terminal.
 *
 * ## De dónde sale cada uno
 *
 * La migración de Panamá (`san-migracion-panama`, 28.2.2.1089, la que está en
 * PAN DEV) arma el XML para la DGI en `Codeunit 57000 - Facturacion Electronica
 * Panama.al`, y **lo arma con la ficha del cliente, no con la cabecera**. De la
 * ficha salen el tipo de cliente, el RUC, el tipo de contribuyente, el dígito
 * verificador, el código de ubicación, el nombre, el teléfono y el correo. De la
 * cabecera sólo salen la dirección y los nombres de provincia, distrito y
 * corregimiento, que Business Central copia a su vez de la ficha al crear el
 * documento.
 *
 * Por eso casi todo lo de Panamá vive en el cliente: es donde lo lee quien arma
 * el comprobante.
 *
 * ## Los nombres de las claves
 *
 * Son los que se proponen a Business Central en
 * `12 - Panamá/campos-excpan-tpv-2026-09-17.md`. Hoy ExceliaOne 1.0.4.123 **no los
 * lee ni los publica**: una clave desconocida no da error, se ignora. El terminal
 * los manda ya, y los lee en cuanto `ListaClientes` los publique.
 */

export const PAIS_PANAMA = COUNTRY_CODES.PANAMA;

/**
 * El tipo de receptor de la DGI (B401), con los nombres del campo `EXCPANTipo
 * Cliente FE` de Business Central: al escribir, BC acepta el nombre del valor.
 * Con el campo vacío la migración no registra la factura --«Debe seleccionar un
 * Tipo Cliente FE en el cliente»--.
 */
export const TIPOS_DE_CLIENTE_FE = {
  CONTRIBUYENTE: "Contribuyente",
  CONSUMIDOR_FINAL: "Consumidor Final",
  GOBIERNO: "Gobierno",
  EXTRANJERO: "Extranjero",
};

/** Para el selector, con el código de la DGI delante: es como lo nombra la ficha técnica. */
export const OPCIONES_DE_TIPO_DE_CLIENTE_FE = [
  { value: TIPOS_DE_CLIENTE_FE.CONTRIBUYENTE, label: "01 · Contribuyente" },
  { value: TIPOS_DE_CLIENTE_FE.CONSUMIDOR_FINAL, label: "02 · Consumidor final" },
  { value: TIPOS_DE_CLIENTE_FE.GOBIERNO, label: "03 · Gobierno" },
  { value: TIPOS_DE_CLIENTE_FE.EXTRANJERO, label: "04 · Extranjero" },
];

const VALORES_DE_TIPO = Object.values(TIPOS_DE_CLIENTE_FE);

/** Sólo contribuyente y gobierno llevan dígito verificador en el XML (57000:774). */
export const llevaDigitoVerificador = (tipoDeCliente) =>
  tipoDeCliente === TIPOS_DE_CLIENTE_FE.CONTRIBUYENTE ||
  tipoDeCliente === TIPOS_DE_CLIENTE_FE.GOBIERNO;

/**
 * El tipo de cliente como lo guarda el terminal.
 *
 * Business Central devuelve el caption y lo escribe por el nombre, que aquí
 * coinciden salvo en mayúsculas. Un valor que no es de la lista se devuelve tal
 * cual: esconderlo haría pasar por vacío algo que está mal escrito.
 */
export const normalizarTipoDeCliente = (valor) => {
  const texto = String(valor ?? "").trim();
  if (!texto) return "";
  return VALORES_DE_TIPO.find((v) => v.toLowerCase() === texto.toLowerCase()) ?? texto;
};

/**
 * El dígito verificador con dos cifras.
 *
 * La DGI lo pide numérico de 2 (`dDV`, B4023), y la propia migración rellena con un
 * cero a la izquierda el que le devuelve la consulta de RUC
 * (`ConsultarRucDV`, 57000:266-269). Si no son sólo dígitos se devuelve tal cual,
 * para que la validación lo vea.
 */
export const normalizarDigitoVerificador = (valor) => {
  const texto = String(valor ?? "").trim();
  if (/^\d$/.test(texto)) return `0${texto}`;
  return texto;
};

/**
 * Las claves de Panamá que viajan con el cliente a `Importaciones_insertaClientes`.
 *
 * Si hay código de ubicación del catálogo, también se mandan la provincia, el
 * distrito y el corregimiento con sus nombres de la DGI. Son los que Business
 * Central copia a la cabecera y pone en el XML (`provincia` ← County, `distrito` ←
 * City, `corregimiento` ← Address 2), así que tienen que decir lo mismo que el
 * código: un código de Bella Vista con «Calidonia» en la dirección 2 es un
 * comprobante que se contradice.
 *
 * Con un código fuera del catálogo no se toca lo que el cajero escribió: no hay
 * nombres de la DGI que poner.
 *
 * @param {object} cliente  La ficha local.
 * @returns {object} Sólo las claves que hay que mandar.
 */
export const clavesDelClienteParaBC = (cliente) => {
  // No se manda lo que no se sabe.
  //
  // Es la misma regla que al leer, y hace falta por lo mismo: desde la 1.0.4.127
  // ExceliaOne escribe la clave que viaja, y distingue «no viene» de «viene
  // vacía» --lo segundo es un borrado a propósito--. Si el terminal mandara
  // siempre las tres, una ficha local sin ubicación **vaciaría la de Business
  // Central**, que la tiene en el 98 % de las fichas de Panamá (medido el
  // 18/09/2026 sobre 1.000). El cajero no puede borrar desde aquí un dato que
  // nunca tuvo.
  const claves = {};
  const poner = (clave, valor) => {
    const texto = String(valor ?? "").trim();
    if (texto) claves[clave] = texto;
  };

  poner("Tipo_Cliente_FE", normalizarTipoDeCliente(cliente?.Tipo_Cliente_FE));
  poner("Ruc_DV", normalizarDigitoVerificador(cliente?.Ruc_DV));
  poner("Codigo_Ubicacion_FE", String(cliente?.Codigo_Ubicacion_FE ?? "").trim());

  const ubicacion = ubicacionDelCodigo(claves.Codigo_Ubicacion_FE);
  if (ubicacion) {
    claves.Provincia = ubicacion.provincia;
    claves.Poblacion = ubicacion.distrito;
    claves.Direccion_2 = ubicacion.corregimiento;
  }

  // El RUC viaja sin espacios alrededor. En PAN DEV, las facturas de clientes con
  // espacios en el RUC se quedaron sin CUFE 15 veces de 75 (20 %), y las de RUC
  // limpio 44 de 2.925 (1,5 %) (`test-2026-09-17/cruce-cufe-PAN-DEV.log`).
  const ruc = String(cliente?.Numero_Identificacion_fiscal ?? "");
  if (ruc && ruc !== ruc.trim()) claves.Numero_Identificacion_fiscal = ruc.trim();

  return claves;
};

/**
 * Si el padrón de Panamá que tiene Business Central ya está al día.
 *
 * Hoy **no**. Los campos de la DGI se capturan en el terminal desde el 17/09/2026,
 * pero ExceliaOne no los sabía escribir hasta la 1.0.4.127 y en PAN DEV sigue
 * instalada una anterior. O sea que hay fichas con el tipo de cliente, el DV y la
 * ubicación puestos aquí que **nunca llegaron allá**.
 *
 * Mientras esto sea `false`, un valor vacío de Business Central **no pisa** uno que
 * el terminal sí tiene. Se pierde poder vaciar un campo desde Business Central
 * --que en la fase 1 no le hace falta a nadie-- y se gana no destruir en la primera
 * lectura justo los datos que se están juntando para la fase 2.
 *
 * Se pone en `true` después de: instalar la versión que escribe, forzar el reenvío
 * de las fichas de Panamá y comprobar que llegaron. Pendiente 48 de
 * `pendientes-vivos.md`. Acordado con la sesión de ExceliaOne el 17/09/2026.
 */
export const EL_PADRON_DE_PANAMA_ESTA_AL_DIA = false;

/**
 * Lo que se lee de `ListaClientes` para Panamá.
 *
 * Tres estados, no dos: si Business Central **no publica** la clave se conserva lo
 * que el terminal ya tenía; si la publica vacía, eso es un dato («no tiene») y se
 * escribe --salvo el cinturón de arriba--. Así «no se sabe» no se confunde con
 * «falta», que es lo que decide si una venta se puede bloquear.
 *
 * Esto se llama **sólo para Panamá** (`clientSyncService.js:169`), y tiene que
 * seguir siendo así: desde la 1.0.4.128 las tres propiedades existen en la página
 * de todos los países, y fuera de Panamá llegan siempre vacías porque el campo no
 * existe allá. Leerlas en otro país sería entender «no tiene» donde no hay nada
 * que tener.
 *
 * @param {object} deBC       El cliente tal como lo devuelve Business Central.
 * @param {object} [local]    La ficha local, si ya existía.
 */
export const clavesDelClienteDesdeBC = (deBC, local) => {
  const leer = (clave, normalizar = (v) => v) => {
    if (deBC?.[clave] === undefined) return local?.[clave];
    const deAlla = normalizar(deBC[clave]);

    // El cinturón de la transición. Mientras el padrón no esté al día, un valor
    // vacío de Business Central no pisa uno que el terminal sí tiene.
    if (!EL_PADRON_DE_PANAMA_ESTA_AL_DIA && !String(deAlla ?? "").trim() && local?.[clave]) {
      return local[clave];
    }

    return deAlla;
  };

  return {
    Tipo_Cliente_FE: leer("Tipo_Cliente_FE", normalizarTipoDeCliente),
    Ruc_DV: leer("Ruc_DV", normalizarDigitoVerificador),
    Codigo_Ubicacion_FE: leer("Codigo_Ubicacion_FE", (v) => String(v ?? "").trim()),
    Provincia: leer("Provincia", (v) => String(v ?? "")),
  };
};

/**
 * Lo fiscal de Panamá de un documento traído de Business Central.
 *
 * Los nombres son los que publican las páginas de la migración de Panamá, medidos
 * en PAN DEV el 17/09/2026 sobre 3.000 facturas y 2.000 abonos registrados:
 *
 * | Campo local | Factura | Abono | Qué trae |
 * |---|---|---|---|
 * | `CUFE` | `EXCPANCUFE` | `EXCPANCUFE` | El código de la DGI, 66 caracteres. Lo tienen 2.941 de 3.000. |
 * | `Numero_Fiscal` | `EXCPANFiscalDocumentNo` | `EXCPANFiscalDocumentNo` | El número de documento fiscal, 10 dígitos. En los borradores, `EXCPANFiscalNo`. |
 * | `Tipo_Documento_DGI` | `EXCPANInvoiceDocumentType` | `EXCPANCrMemoDocumentType` | «Factura de operacion interna», «Nota de Credito referente a una FE»… |
 * | `Tipo_Emision_DGI` | `EXCPANEmissionType` | `EXCPANEmissionType` | «Operacion Normal» en todas. |
 * | `Numero_Fiscal_Referencia` | — | `EXCPANElectronicReferenceNo` | El número fiscal de la factura que corrige. |
 *
 * `?? undefined`: la página que no publique un campo no pisa la copia local. Un
 * CUFE vacío en una registrada sí se escribe, porque es una afirmación: no lo
 * tiene.
 */
export const camposFiscalesDelDocumento = (apiObj) => ({
  CUFE: apiObj?.EXCPANCUFE ?? undefined,
  Numero_Fiscal: apiObj?.EXCPANFiscalDocumentNo ?? apiObj?.EXCPANFiscalNo ?? undefined,
  Tipo_Documento_DGI:
    apiObj?.EXCPANInvoiceDocumentType ??
    apiObj?.EXCPANCrMemoDocumentType ??
    apiObj?.EXCPANCreditMemoDocType ??
    undefined,
  Tipo_Emision_DGI: apiObj?.EXCPANEmissionType ?? undefined,
  Numero_Fiscal_Referencia:
    apiObj?.EXCPANElectronicReferenceNo ?? apiObj?.EXCPANFEReferenceNo ?? undefined,
});
